import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  listInvitesForUser,
  respondToOrganisationInvite,
  listOrganisationInvites,
} from '@/client/sdk.gen';
import type { OrganisationInviteRead } from '@/client/types.gen';
import DashboardLayout from '@/app/dashboard/layout';
import { Loader2 } from 'lucide-react';
import { useOrganisation } from '@/App';
import { SELECTED_ORG_KEY } from '@/constants';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

// Create a custom event to refresh pending invites in the sidebar
const refreshPendingInvitesEvent = new CustomEvent('refresh-pending-invites');

export default function OrganisationInvites() {
  const { toast } = useToast();
  const [invites, setInvites] = useState<OrganisationInviteRead[]>([]);
  const [loading, setLoading] = useState(true);
  const [acceptingInvite, setAcceptingInvite] = useState<string | null>(null);
  const [showSwitchOrgDialog, setShowSwitchOrgDialog] = useState(false);
  const [acceptedOrgDetails, setAcceptedOrgDetails] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const { switchOrganisation } = useOrganisation();

  const loadInvites = async () => {
    try {
      const response = await listInvitesForUser();
      if (response.data) {
        setInvites(response.data.invites);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load invites. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvites();
  }, []);

  const handleAcceptInvite = async (invite: OrganisationInviteRead) => {
    setAcceptingInvite(invite.id);
    try {
      await respondToOrganisationInvite({
        body: {
          invite_id: invite.id,
          accept: true,
        },
      });

      toast({
        title: 'Success',
        description: 'Invitation accepted successfully.',
      });

      // Trigger refresh event to update the sidebar's pending invites
      window.dispatchEvent(refreshPendingInvitesEvent);

      // Store accepted org details for the dialog
      setAcceptedOrgDetails({
        id: invite.organisation_id,
        name: invite.email, // Using email as a fallback since we don't have the org name
      });

      // Show the switch org dialog
      setShowSwitchOrgDialog(true);

      // Refresh the invites list
      loadInvites();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to accept invitation. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setAcceptingInvite(null);
    }
  };

  const handleSwitchToNewOrg = async () => {
    if (acceptedOrgDetails) {
      try {
        await switchOrganisation(acceptedOrgDetails.id);
        // The page will reload due to the switchOrganisation implementation
      } catch (error) {
        toast({
          title: 'Error',
          description:
            'Failed to switch to the new team. Please try again later.',
          variant: 'destructive',
        });
      }
    }
    setShowSwitchOrgDialog(false);
  };

  return (
    <DashboardLayout
      breadcrumbs={[
        { label: 'Home', href: '/dashboard' },
        { label: 'Team Settings', href: '/dashboard/org-settings' },
        { label: 'Invites', href: '/dashboard/org-settings/invites' },
      ]}
    >
      <div className="p-6">
        <Card>
          <CardHeader>
            <CardTitle>Team Invites</CardTitle>
            <CardDescription>
              View and manage your pending team invitations
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Team</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Invited By</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-4">
                      <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : invites.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-4">
                      No pending invitations
                    </TableCell>
                  </TableRow>
                ) : (
                  invites.map((invite) => (
                    <TableRow key={invite.id}>
                      <TableCell>{invite.email}</TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            invite.role === 'ADMIN' ? 'default' : 'secondary'
                          }
                        >
                          {invite.role}
                        </Badge>
                      </TableCell>
                      <TableCell>{invite.invited_by_id}</TableCell>
                      <TableCell>
                        {new Date(invite.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {invite.accepted_at ? (
                          <Badge variant="default">Accepted</Badge>
                        ) : (
                          <Badge variant="secondary">Pending</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {!invite.accepted_at && (
                          <Button
                            size="sm"
                            onClick={() => handleAcceptInvite(invite)}
                            disabled={acceptingInvite === invite.id}
                          >
                            {acceptingInvite === invite.id && (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            )}
                            Accept
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Dialog for switching to the new organization */}
      <Dialog open={showSwitchOrgDialog} onOpenChange={setShowSwitchOrgDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Team Joined Successfully</DialogTitle>
            <DialogDescription>
              You've successfully joined the team. Would you like to switch to
              this team now?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex justify-between sm:justify-between mt-4">
            <Button
              variant="outline"
              onClick={() => setShowSwitchOrgDialog(false)}
            >
              Later
            </Button>
            <Button onClick={handleSwitchToNewOrg}>Switch Now</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
